import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Plus, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import EmployeeFormDialog from "@/components/directory/EmployeeFormDialog";
import OffboardConfirmDialog from "@/components/directory/OffboardConfirmDialog";

const initials = (n) =>
  (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

export default function DirectoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [employees, setEmployees] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // employee object or null (=create)
  const [offboardTarget, setOffboardTarget] = useState(null);

  const loadDepts = () => api.get("/employees/departments").then((r) => setDepts(r.data));
  const loadEmployees = () =>
    api
      .get("/employees", { params: { q, department: dept } })
      .then((r) => setEmployees(r.data));

  useEffect(() => { loadDepts(); }, []);
  useEffect(() => { loadEmployees(); }, [q, dept]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (emp) => { setEditing(emp); setFormOpen(true); };

  const onSaved = () => {
    loadEmployees();
    loadDepts();
  };

  const confirmOffboard = async () => {
    if (!offboardTarget) return;
    try {
      await api.delete(`/employees/${offboardTarget.id}`);
      toast.success(`${offboardTarget.name} offboarded`);
      setOffboardTarget(null);
      loadEmployees();
      loadDepts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Offboard failed");
    }
  };

  const headerCount = useMemo(() => employees.length, [employees]);

  return (
    <div className="space-y-6" data-testid="directory-page">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="overline">People</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">
            Employee directory
          </h1>
          <p className="text-sm text-[#575752]">
            {headerCount} colleagues across the company.
          </p>
        </div>
        {isAdmin && (
          <Button
            data-testid="directory-add-btn"
            onClick={openCreate}
            className="bg-[#4a6b53] text-white hover:bg-[#3d5a45]"
          >
            <Plus strokeWidth={1.5} className="mr-1.5 h-4 w-4" /> Add employee
          </Button>
        )}
      </header>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a8a80]"
          />
          <Input
            data-testid="directory-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or role…"
            className="pl-9 focus-visible:ring-[#4a6b53]/30"
          />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="md:w-56" data-testid="directory-department-select">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {depts.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        data-testid="directory-grid"
      >
        {employees.map((e) => (
          <div
            key={e.id}
            className="group relative flex flex-col rounded-lg border border-[#e5e5e0] bg-white p-5 transition-colors duration-200 ease-in-out hover:border-[#d1d1ca]"
            data-testid={`employee-card-${e.id}`}
          >
            {isAdmin && (
              <div className="absolute right-3 top-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`employee-actions-${e.id}`}
                      className="h-7 w-7 text-[#575752] hover:bg-[#f0f0ed]"
                      aria-label="Employee actions"
                    >
                      <MoreHorizontal strokeWidth={1.5} className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      data-testid={`employee-edit-${e.id}`}
                      onClick={() => openEdit(e)}
                    >
                      <Pencil strokeWidth={1.5} className="mr-2 h-4 w-4" />
                      Edit profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid={`employee-offboard-${e.id}`}
                      onClick={() => setOffboardTarget(e)}
                      className="text-[#8f2c2c] focus:text-[#8f2c2c]"
                    >
                      <Trash2 strokeWidth={1.5} className="mr-2 h-4 w-4" />
                      Offboard
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14 rounded-md">
                <AvatarImage src={e.avatar} alt={e.name} />
                <AvatarFallback className="rounded-md bg-[#eaf0ec] text-[#3d5a45]">
                  {initials(e.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 pr-8">
                <div className="truncate font-display text-base font-bold">
                  {e.name}
                </div>
                <div className="mt-0.5 truncate font-mono text-[11px] text-[#575752]">
                  {e.role_title}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="rounded-full bg-[#f0f0ed] px-2 py-0.5 text-[10px] font-medium text-[#575752]">
                {e.department}
              </span>
              <span className="rounded-full bg-[#eaf0ec] px-2 py-0.5 text-[10px] font-medium text-[#3d5a45]">
                {e.office}
              </span>
            </div>
            <Link
              to={`/app/directory/${e.id}`}
              data-testid={`employee-view-${e.id}`}
              className="mt-5 inline-block w-full rounded-md border border-[#e5e5e0] py-2 text-center text-xs font-medium text-[#1c1c19] transition-colors duration-200 ease-in-out hover:bg-[#f0f0ed]"
            >
              View profile
            </Link>
          </div>
        ))}
        {employees.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-[#e5e5e0] p-10 text-center text-sm text-[#575752]">
            No matching colleagues found.
          </div>
        )}
      </div>

      <EmployeeFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        employee={editing}
        onSaved={onSaved}
      />
      <OffboardConfirmDialog
        open={Boolean(offboardTarget)}
        onOpenChange={(o) => !o && setOffboardTarget(null)}
        employeeName={offboardTarget?.name || ""}
        onConfirm={confirmOffboard}
      />
    </div>
  );
}
